/**
 * AI Summary functionality for SWPTLS tables
 * Handles AI-powered table summaries with server-side pagination support
 * ES6 Module Version
 */

// Define AISummary class outside jQuery ready block
class AISummary {
    constructor() {
        this.isGenerating = false;
        this.summaryCache = new Map();
        this.$ = jQuery; // Store jQuery reference
        this.init();
    }

    init() {
        this.bindEvents();
    }

    bindEvents() {
        const $ = this.$;
        // Handle AI summary button clicks
        $(document).on('click', '.swptls-ai-summary-btn', (e) => {
            e.preventDefault();
            const tableId = $(e.currentTarget).data('table-id');
            const tableElement = $(e.currentTarget).closest('.gswpts_tables_container').find('table');
            if (tableId && tableElement.length) {
                this.generateSummary(tableId, tableElement);
            }
        });

        // Handle summary modal close
        $(document).on('click', '.ai-summary-modal .close-modal, .ai-summary-modal .modal-overlay', (e) => {
            if (e.target === e.currentTarget) {
                this.closeSummaryModal();
            }
        });

        // Handle ESC key to close modal
        $(document).on('keydown', (e) => {
            if (e.key === 'Escape') {
                this.closeSummaryModal();
            }
        });

        // Handle regenerate summary
        $(document).on('click', '.regenerate-summary-btn', (e) => {
            e.preventDefault();
            const tableId = $(e.currentTarget).data('table-id');
            const button = $(e.currentTarget);
            // Show regenerating state
            this.showRegeneratingState(button);
            // Try multiple ways to find the table element
            let tableElement = $(`.gswpts_tables_container[id="${tableId}"] table`);
            if (tableElement.length === 0) {
                tableElement = $(`.gswpts_table_${tableId} table`);
            }
            if (tableElement.length === 0) {
                tableElement = $(`#${tableId} table`);
            }
            if (tableElement.length === 0) {
                tableElement = $('.gswpts_tables_container table').first();
            }
            if (tableElement.length > 0) {
                // Show regenerating message in modal
                this.showRegeneratingInModal();
                this.generateSummary(tableId, tableElement, true); // Force regenerate
            } else {
                console.error('Could not find table element for regeneration');
                this.showSummaryError(tableId, 'Could not find table data for regeneration.');
                this.hideRegeneratingState(button);
            }
        });
    }

    addSummaryButton(container, tableId) {
        // No longer needed - AI Summary buttons are now rendered on the backend
        // console.log("AI Summary button rendering is now handled by backend");
    }

    generateSummary(tableId, tableElement, forceRegenerate = false) {
        if (this.isGenerating) {
            return;
        }

        // Check if caching is enabled for this table
        const enableCache = this.getTableSpecificSetting(tableId, 'enable_ai_cache');
        // console.log(`AI Summary cache setting for table ${tableId}:`, enableCache);

        // Generate cache key
        let cacheKey = this.generateCacheKey(tableId, tableElement);
        // console.log(`Generated cache key: ${cacheKey}`);

        // If forcing regenerate, clear ALL related cache entries
        if (forceRegenerate) {
            // console.log('Force regenerating - clearing all caches for table:', tableId);
            // Clear existing cache entry
            this.summaryCache.delete(cacheKey);
            // Clear any regenerate cache entries that might exist
            const cacheKeys = Array.from(this.summaryCache.keys());
            cacheKeys.forEach(key => {
                if (key.startsWith(`ai_summary_${tableId}_`)) {
                    // console.log('Clearing frontend cache key:', key);
                    this.summaryCache.delete(key);
                }
            });
            // Add timestamp to make cache key unique for regeneration
            cacheKey = cacheKey + '_regenerate_' + Date.now();
        } else if (enableCache && this.summaryCache.has(cacheKey)) {
            // Check cache first (only if caching is enabled and not forcing regenerate)
            // console.log('Using cached summary for key:', cacheKey);
            this.showSummaryModal(this.summaryCache.get(cacheKey), tableId);
            return;
        } else if (!enableCache) {
            // console.log('Caching disabled for table, bypassing cache check');
        }

        this.isGenerating = true;
        this.showGeneratingProgress(tableId);

        try {
            // Try to get all data from DataTable API first
            const tableData = this.collectAllTableDataFromDataTable(tableElement);

            // Get table settings - prefer stored settings from table configuration
            // First try to get from global window.swptlsTableSettings (set by gswpts_sheet_fetch)
            let table_settings = null;
            if (window.swptlsTableSettings && window.swptlsTableSettings[tableId]) {
                table_settings = window.swptlsTableSettings[tableId];
                // console.log('Using table settings from window.swptlsTableSettings:', table_settings);
            } else {
                // console.log('No table settings found in window.swptlsTableSettings for table:', tableId);
            }

            const tableSettings = this.getTableSettings(tableId, table_settings);
            // console.log('Final table settings for AI summary:', tableSettings);

            // Send to backend for AI processing
            this.requestAISummary(tableId, tableData, tableSettings, cacheKey, forceRegenerate);
        } catch (error) {
            console.error('AI Summary Error:', error);
            this.showSummaryError(tableId, 'Failed to prepare table data for AI summary.');
            this.isGenerating = false;
        }
    }

    collectAllTableDataFromDataTable(tableElement) {
        const $ = this.$;
        const data = {
            headers: [],
            rows: [],
            title: '',
            totalVisibleRows: 0,
            tableStructure: {}
        };

        // Get table title
        const titleElement = tableElement.closest('.gswpts_tables_container').find('.table-title, h2, h3');
        if (titleElement.length) {
            data.title = titleElement.first().text().trim();
        }

        // Try to get DataTable instance with better error handling
        let dataTable = null;
        let isDataTableReady = false;

        try {
            // Check if DataTable is initialized and ready
            if ($.fn.DataTable.isDataTable(tableElement)) {
                dataTable = $(tableElement).DataTable();
                // Test if DataTable is fully functional
                dataTable.rows().count();
                isDataTableReady = true;
                // console.log('AI Summary: DataTable instance found and ready');
            }
        } catch (e) {
            // console.log('AI Summary: DataTable not ready or accessible, using DOM parsing:', e.message);
            isDataTableReady = false;
        }

        // Get headers with column information
        const headerCells = tableElement.find('thead tr:first th, thead tr:first td');
        headerCells.each((index, element) => {
            const headerText = $(element).text().trim();
            if (headerText) {
                data.headers.push({
                    index: index,
                    text: headerText,
                    type: this.detectColumnType(tableElement, index)
                });
            }
        });

        if (dataTable && isDataTableReady) {
            // Use DataTable API to get ALL data (not just visible)
            try {
                const allData = dataTable.rows().data();
                data.totalVisibleRows = allData.length;

                // Process all rows from DataTable
                allData.each((rowData) => {
                    const row = [];
                    for (let i = 0; i < rowData.length; i++) {
                        let cellText = '';

                        // Handle different data types from DataTable
                        if (typeof rowData[i] === 'string') {
                            cellText = rowData[i];
                        } else if (rowData[i] && rowData[i].innerHTML) {
                            cellText = rowData[i].innerHTML;
                        } else if (rowData[i] && rowData[i].textContent) {
                            cellText = rowData[i].textContent;
                        } else {
                            cellText = String(rowData[i] || '');
                        }

                        // Clean up HTML and extract text content
                        const $temp = $('<div>').html(cellText);

                        // Handle special content types
                        const img = $temp.find('img');
                        if (img.length) {
                            const altText = img.attr('alt') || 'Image';
                            cellText = `[Image: ${altText}]`;
                        } else {
                            const link = $temp.find('a');
                            if (link.length && !cellText.trim()) {
                                cellText = `[Link: ${link.text().trim() || 'URL'}]`;
                            } else {
                                cellText = $temp.text().trim();
                            }
                        }

                        // Limit cell content length to prevent token overflow
                        if (cellText.length > 100) {
                            cellText = cellText.substring(0, 97) + '...';
                        }
                        row.push(cellText || '');
                    }
                    if (row.length > 0) {
                        data.rows.push(row);
                    }
                });

                // console.log(`AI Summary: Collected ${data.rows.length} rows from DataTable API`);

            } catch (e) {
                // console.log('AI Summary: Error accessing DataTable data, falling back to DOM parsing:', e);
                // Fallback to DOM parsing
                return this.collectCurrentTableData(tableElement);
            }
        } else {
            // Fallback to DOM parsing if DataTable not available
            // console.log('AI Summary: Using DOM parsing as fallback');
            return this.collectCurrentTableData(tableElement);
        }

        // Add table structure information
        data.tableStructure = {
            totalColumns: data.headers.length,
            totalVisibleRows: data.totalVisibleRows,
            sampleSize: data.rows.length,
            hasImages: tableElement.find('img').length > 0,
            hasLinks: tableElement.find('a').length > 0,
            hasPagination: tableElement.closest('.gswpts_tables_container').find('.pagination, .load-more-btn').length > 0
        };

        return data;
    }

    collectCurrentTableData(tableElement) {
        const $ = this.$;
        const data = {
            headers: [],
            rows: [],
            title: '',
            totalVisibleRows: 0,
            tableStructure: {}
        };

        // Get table title
        const titleElement = tableElement.closest('.gswpts_tables_container').find('.table-title, h2, h3');
        if (titleElement.length) {
            data.title = titleElement.first().text().trim();
        }

        // Get headers with column information
        const headerCells = tableElement.find('thead tr:first th, thead tr:first td');
        headerCells.each((index, element) => {
            const headerText = $(element).text().trim();
            if (headerText) {
                data.headers.push({
                    index: index,
                    text: headerText,
                    type: this.detectColumnType(tableElement, index)
                });
            }
        });

        // Get currently visible rows (respects pagination and filters)
        const visibleRows = tableElement.find('tbody tr:visible');
        data.totalVisibleRows = visibleRows.length;

        // Collect ALL visible rows for AI analysis - no artificial limits
        // The backend will handle token limits and chunking if needed
        const sampleRows = visibleRows;
        sampleRows.each((index, element) => {
            const row = [];
            $(element).find('td').each((cellIndex, cellElement) => {
                let cellText = $(cellElement).text().trim();

                // Handle special content types
                const img = $(cellElement).find('img');
                if (img.length) {
                    const altText = img.attr('alt') || 'Image';
                    cellText = `[Image: ${altText}]`;
                }

                const link = $(cellElement).find('a');
                if (link.length && !cellText) {
                    cellText = `[Link: ${link.text().trim() || 'URL'}]`;
                }

                // Limit cell content length to prevent token overflow
                if (cellText.length > 100) {
                    cellText = cellText.substring(0, 97) + '...';
                }

                row.push(cellText || '');
            });
            if (row.length > 0) {
                data.rows.push(row);
            }
        });

        // Add table structure information
        data.tableStructure = {
            totalColumns: data.headers.length,
            totalVisibleRows: data.totalVisibleRows,
            sampleSize: data.rows.length,
            hasImages: tableElement.find('img').length > 0,
            hasLinks: tableElement.find('a').length > 0,
            hasPagination: tableElement.closest('.gswpts_tables_container').find('.pagination, .load-more-btn').length > 0
        };

        return data;
    }

    detectColumnType(tableElement, columnIndex) {
        const $ = this.$;
        // Analyze first few cells to detect column type
        const cells = tableElement.find(`tbody tr:visible td:nth-child(${columnIndex + 1})`).slice(0, 5);
        let hasNumbers = 0;
        let hasDates = 0;
        let hasImages = 0;
        let hasLinks = 0;

        cells.each((index, element) => {
            const text = $(element).text().trim();
            if ($(element).find('img').length > 0) hasImages++;
            if ($(element).find('a').length > 0) hasLinks++;
            if (!isNaN(parseFloat(text)) && isFinite(text)) hasNumbers++;
            if (this.isDateString(text)) hasDates++;
        });

        if (hasImages > 0) return 'image';
        if (hasLinks > 0) return 'link';
        if (hasNumbers >= cells.length * 0.6) return 'number';
        if (hasDates >= cells.length * 0.6) return 'date';
        return 'text';
    }

    isDateString(str) {
        return !isNaN(Date.parse(str)) && str.match(/\d{1,4}[-\/]\d{1,2}[-\/]\d{1,4}/);
    }

    sampleRows(rows, maxRows) {
        const $ = this.$;

        if (rows.length <= maxRows) {
            return rows;
        }

        // Intelligent sampling: take first few, last few, and some from middle
        const firstRows = Math.floor(maxRows * 0.4);
        const lastRows = Math.floor(maxRows * 0.3);
        const middleRows = maxRows - firstRows - lastRows;
        const sampled = [];

        // First rows
        for (let i = 0; i < firstRows && i < rows.length; i++) {
            sampled.push(rows[i]);
        }

        // Middle rows (evenly distributed)
        if (middleRows > 0 && rows.length > firstRows + lastRows) {
            const middleStart = firstRows;
            const middleEnd = rows.length - lastRows;
            const step = Math.floor((middleEnd - middleStart) / middleRows);

            for (let i = 0; i < middleRows; i++) {
                const index = middleStart + (i * step);
                if (index < middleEnd) {
                    sampled.push(rows[index]);
                }
            }
        }

        // Last rows
        for (let i = Math.max(0, rows.length - lastRows); i < rows.length; i++) {
            sampled.push(rows[i]);
        }

        return $(sampled);
    }

    getTableSettings(tableId, table_settings = null) {
        const $ = this.$;
        const container = $(`.gswpts_tables_container table[id*="${tableId}"]`).closest('.gswpts_tables_container');

        // Default AI settings
        const defaultAISettings = {
            show_table_prompt_fields: false,
            ask_ai_placeholder: 'Ask anything about this table… e.g., Top 5 products by sales',
            ask_ai_button_label: 'Ask AI',
            backend_ai_summary: '',
            backend_summary_exists: false,
            show_regenerate_button: false,
            enable_backend_ai_trigger: false,
            edit_summary_content: false,
            show_summary_in_table: false,
            summary_prompt: 'Give a short summary of this table (max 50 words), highlighting key takeaways and trends.',
            enable_ai_cache: true,
            enable_ai_summary: false
        };

        // If table_settings are provided as parameter (from gswpts_sheet_fetch response), use them
        let tableSpecificSettings = { ...defaultAISettings };
        if (table_settings) {
            // Merge provided settings with defaults, giving priority to provided settings
            tableSpecificSettings = {
                ...defaultAISettings,
                ...table_settings
            };
        } else {
            // Fallback: Try to get from global window.swptlsTableSettings first
            if (window.swptlsTableSettings && window.swptlsTableSettings[tableId]) {
                tableSpecificSettings = {
                    ...defaultAISettings,
                    ...window.swptlsTableSettings[tableId]
                };
            } else {
                // Last resort: Get table-specific AI settings from data-table_settings attribute
                try {
                    const tableSettingsJson = container.attr('data-table_settings');
                    if (tableSettingsJson) {
                        const tableSettings = JSON.parse(tableSettingsJson);
                        tableSpecificSettings = {
                            ...defaultAISettings,
                            ...tableSettings
                        };
                    }
                } catch (e) {
                    console.warn('Failed to parse table settings for table:', tableId, e);
                }
            }
        }

        // Debug log to verify prompt is being captured
        if (tableSpecificSettings.summary_prompt !== defaultAISettings.summary_prompt) {
            console.log('Custom prompt detected:', tableSpecificSettings.summary_prompt);
        }

        return {
            tableId: tableId,
            hasTitle: container.find('.table-title').length > 0,
            hasPagination: container.find('.pagination, .load-more-btn').length > 0,
            hasFilters: container.find('.dataTables_filter, .search-input').length > 0,
            theme: (() => {
                const className = container.attr('class');
                if (className && className.match(/gswpts_style-\d+/)) {
                    const match = className.match(/gswpts_style-(\d+)/);
                    return match ? match[1] : 'default';
                }
                return 'default';
            })(),
            // Include table-specific AI settings with defaults
            ai_settings: tableSpecificSettings
        };
    }

    getTableSpecificSetting(tableId, settingKey) {
        const $ = this.$;

        // Default AI settings
        const defaults = {
            show_table_prompt_fields: false,
            ask_ai_placeholder: 'Ask anything about this table… e.g., Top 5 products by sales',
            ask_ai_button_label: 'Ask AI',
            backend_ai_summary: '',
            backend_summary_exists: false,
            show_regenerate_button: false,
            enable_backend_ai_trigger: false,
            edit_summary_content: false,
            show_summary_in_table: false,
            summary_prompt: 'Give a short summary of this table (max 50 words), highlighting key takeaways and trends.',
            enable_ai_cache: true,
            enable_ai_summary: false
        };

        // First try to get from stored table settings (preferred method)
        if (window.swptlsTableSettings && window.swptlsTableSettings[tableId]) {
            const table_settings = window.swptlsTableSettings[tableId];
            if (table_settings.hasOwnProperty(settingKey)) {
                return table_settings[settingKey];
            }
        }

        // Fallback to parsing from DOM attribute
        const container = $(`.gswpts_tables_container table[id*="${tableId}"]`).closest('.gswpts_tables_container');

        try {
            const tableSettingsJson = container.attr('data-table_settings');
            if (tableSettingsJson) {
                const tableSettings = JSON.parse(tableSettingsJson);

                // Return table-specific setting if it exists
                if (tableSettings.hasOwnProperty(settingKey)) {
                    return tableSettings[settingKey];
                }
            }
        } catch (e) {
            console.warn('Failed to parse table settings for table:', tableId, e);
        }

        // Return default value
        return defaults[settingKey] !== undefined ? defaults[settingKey] : null;
    }

    requestAISummary(tableId, tableData, tableSettings, cacheKey, forceRegenerate = false) {
        const formData = new FormData();
        formData.append('action', 'gswpts_generate_ai_summary');
        formData.append('table_id', tableId);
        formData.append('table_data', JSON.stringify(tableData));
        formData.append('table_settings', JSON.stringify(tableSettings));
        formData.append('cache_key', cacheKey);
        formData.append('force_regenerate', forceRegenerate ? '1' : '0');

        // Check if swptls_ajax exists (it might be different in your setup)
        const nonce = front_end_data?.nonce;
        const ajaxUrl = front_end_data?.admin_ajax;

        if (nonce) {
            formData.append('nonce', nonce);
        }

        fetch(ajaxUrl, {
            method: 'POST',
            body: formData
        })
            .then(response => {
                // Check if response is ok before parsing JSON
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }
                return response.json();
            })
            .then(data => {
                // console.log('AI Summary Response:', data); // Debug log
                if (data.success && data.data.summary) {
                    // Check if caching is enabled before storing in frontend cache
                    const enableCache = this.getTableSpecificSetting(tableId, 'enable_ai_cache');
                    if (enableCache) {
                        // Cache the summary only if caching is enabled
                        // console.log('Storing summary in frontend cache with key:', cacheKey);
                        this.summaryCache.set(cacheKey, data.data);

                        // If this was a regeneration, also update the original cache key
                        // so future requests get the latest data
                        if (forceRegenerate && cacheKey.includes('_regenerate_')) {
                            const tableElement = this.findTableElement(tableId);
                            if (tableElement && tableElement.length > 0) {
                                const originalCacheKey = this.generateCacheKey(tableId, tableElement);
                                // console.log('Updating original cache key after regeneration:', originalCacheKey);
                                this.summaryCache.set(originalCacheKey, data.data);
                            }
                        }
                    } else {
                        // console.log('Caching disabled, not storing summary in frontend cache');
                    }
                    // Always show the summary
                    this.showSummaryModal(data.data, tableId);
                } else {
                    // Handle error response - data.data now contains error information
                    this.showSummaryError(tableId, data.data || { message: 'Failed to generate AI summary.', error_type: 'general_error' });
                }
            })
            .catch(error => {
                console.error('AI Summary Generation Error:', error);
                // Check if it's a network error or a parsing error
                let errorMessage = 'Network error while generating summary.';
                if (error.name === 'SyntaxError') {
                    errorMessage = 'Server returned an invalid response. Please try again.';
                } else if (error.message && error.message.includes('fetch')) {
                    errorMessage = 'Unable to connect to the server. Please check your internet connection.';
                }

                this.showSummaryError(tableId, {
                    message: errorMessage,
                    error_type: 'network_error'
                });
            })
            .finally(() => {
                this.isGenerating = false;
                this.hideGeneratingProgress(tableId);
            });
    }

    generateCacheKey(tableId, tableElement) {
        // Generate a cache key based on current table state
        const visibleRows = tableElement.find('tbody tr:visible').length;
        const tableContent = tableElement.find('tbody').text().substring(0, 100);
        const contentHash = this.simpleHash(tableContent);
        return `ai_summary_${tableId}_${visibleRows}_${contentHash}`;
    }

    findTableElement(tableId) {
        const $ = this.$;
        // Try multiple ways to find the table element
        let tableElement = $(`.gswpts_tables_container[id="${tableId}"] table`);
        if (tableElement.length === 0) {
            tableElement = $(`.gswpts_table_${tableId} table`);
        }
        if (tableElement.length === 0) {
            tableElement = $(`#${tableId} table`);
        }
        if (tableElement.length === 0) {
            tableElement = $(`.gswpts_tables_container table[id*="${tableId}"]`);
        }
        if (tableElement.length === 0) {
            tableElement = $('.gswpts_tables_container table').first();
        }
        return tableElement;
    }

    simpleHash(str) {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            const char = str.charCodeAt(i);
            hash = ((hash << 5) - hash) + char;
            hash = hash & hash; // Convert to 32-bit integer
        }
        return Math.abs(hash).toString(36);
    }

    showGeneratingProgress(tableId) {
        const $ = this.$;
        const button = $(`.swptls-ai-summary-btn[data-table-id="${tableId}"]`);

        // Store the original button HTML for restoration later
        if (!button.data('original-html')) {
            button.data('original-html', button.html());
        }

        button.prop('disabled', true)
            .addClass('generating')
            .html(`
                    <span class="ai-icon">
                            <svg class="fWWlmf JzISke" height="24" width="24" aria-hidden="true" viewBox="0 0 471 471" xmlns="http://www.w3.org/2000/svg"><path fill="var(--m3c23)" d="M235.5 471C235.5 438.423 229.22 407.807 216.66 379.155C204.492 350.503 187.811 325.579 166.616 304.384C145.421 283.189 120.498 266.508 91.845 254.34C63.1925 241.78 32.5775 235.5 0 235.5C32.5775 235.5 63.1925 229.416 91.845 217.249C120.498 204.689 145.421 187.811 166.616 166.616C187.811 145.421 204.492 120.497 216.66 91.845C229.22 63.1925 235.5 32.5775 235.5 0C235.5 32.5775 241.584 63.1925 253.751 91.845C266.311 120.497 283.189 145.421 304.384 166.616C325.579 187.811 350.503 204.689 379.155 217.249C407.807 229.416 438.423 235.5 471 235.5C438.423 235.5 407.807 241.78 379.155 254.34C350.503 266.508 325.579 283.189 304.384 304.384C283.189 325.579 266.311 350.503 253.751 379.155C241.584 407.807 235.5 438.423 235.5 471Z"></path>
                            </svg>
                        </span>
                    <span class="button-text">Generating...</span>
                `);
    }

    hideGeneratingProgress(tableId) {
        const $ = this.$;
        const button = $(`.swptls-ai-summary-btn[data-table-id="${tableId}"]`);

        // Restore the original button HTML if it was stored
        const originalHtml = button.data('original-html');

        if (originalHtml) {
            button.prop('disabled', false)
                .removeClass('generating')
                .html(originalHtml);
        } else {
            // Fallback to default if original HTML wasn't stored
            button.prop('disabled', false)
                .removeClass('generating')
                .html(`
                        <span class="ai-icon">
                                <svg class="fWWlmf JzISke" height="24" width="24" aria-hidden="true" viewBox="0 0 471 471" xmlns="http://www.w3.org/2000/svg"><path fill="var(--m3c23)" d="M235.5 471C235.5 438.423 229.22 407.807 216.66 379.155C204.492 350.503 187.811 325.579 166.616 304.384C145.421 283.189 120.498 266.508 91.845 254.34C63.1925 241.78 32.5775 235.5 0 235.5C32.5775 235.5 63.1925 229.416 91.845 217.249C120.498 204.689 145.421 187.811 166.616 166.616C187.811 145.421 204.492 120.497 216.66 91.845C229.22 63.1925 235.5 32.5775 235.5 0C235.5 32.5775 241.584 63.1925 253.751 91.845C266.311 120.497 283.189 145.421 304.384 166.616C325.579 187.811 350.503 204.689 379.155 217.249C407.807 229.416 438.423 235.5 471 235.5C438.423 235.5 407.807 241.78 379.155 254.34C350.503 266.508 325.579 283.189 304.384 304.384C283.189 325.579 266.311 350.503 253.751 379.155C241.584 407.807 235.5 438.423 235.5 471Z"></path>
                                </svg>
                            </span>
                        <span class="button-text hint-icon" title="Click to generate an intelligent summary of the table data using AI">AI Summary</span>
                    `);
        }
    }

    showSummaryModal(summaryData, tableId) {
        const $ = this.$;

        // Check if modal already exists (for regeneration)
        const existingModal = $('.ai-summary-modal');
        if (existingModal.length > 0) {
            // Update existing modal content
            this.updateModalContent(summaryData, tableId);
            // Reset regenerate button state
            const regenerateBtn = existingModal.find('.regenerate-summary-btn');
            this.hideRegeneratingState(regenerateBtn);
        } else {
            // Create new modal
            const modal = this.createSummaryModal(summaryData, tableId);
            $('body').append(modal);
            // Show modal with animation
            setTimeout(() => {
                $('.ai-summary-modal').addClass('show');
            }, 100);
        }
    }

    createSummaryModal(summaryData, tableId) {
        const {
            summary,
            metadata,
            generated_at,
            tokens_used,
            model_used
        } = summaryData;

        // Get table-specific show_regenerate_button setting
        const showRegenerateButton = this.getTableSpecificSetting(tableId, 'show_regenerate_button');

        // console.log('AI Summary Show rege (table-specific):', showRegenerateButton);

        return `
                <div class="ai-summary-modal">
                    <div class="modal-overlay"></div>
                    <div class="modal-content">
                        <div class="modal-header">
                            <h3>Table Summary</h3>
                            <button class="close-modal">&times;</button>
                        </div>
                        
                        <div class="modal-body">
                            <div class="summary-content">
                                ${this.formatSummary(summary)}
                            </div>
                        </div>
                        
                        <div class="modal-footer">
                            ${showRegenerateButton ? `
                            <button class="regenerate-summary-btn" data-table-id="${tableId}">
                                Regenerate
                            </button>
                            ` : ''}
                        </div>
                    </div>
                </div>
            `;
    }

    formatSummary(summary) {
        // Convert markdown-like formatting to HTML
        return summary
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
            .replace(/\*(.*?)\*/g, '<em>$1</em>')
            .replace(/\n\n/g, '</p><p>')
            .replace(/\n/g, '<br>')
            .replace(/^/, '<p>')
            .replace(/$/, '</p>');
    }

    formatDate(dateString) {
        if (!dateString) return 'Just now';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
        return date.toLocaleDateString();
    }

    closeSummaryModal() {
        const $ = this.$;
        $('.ai-summary-modal').removeClass('show');
        setTimeout(() => {
            $('.ai-summary-modal').remove();
        }, 300);
    }

    showSummaryError(tableId, errorData) {
        let message = '';
        let errorType = 'error';

        // Handle different error data formats
        if (typeof errorData === 'string') {
            message = errorData;
        } else if (errorData && typeof errorData === 'object') {
            message = errorData.message || 'Failed to generate AI summary.';
            errorType = errorData.error_type || 'error';
        } else {
            message = 'Failed to generate AI summary.';
        }

        // Customize message and type based on error type
        switch (errorType) {
            case 'rate_limit':
                this.showNotification(message, 'warning', 8000); // Show longer for rate limit
                break;
            case 'quota_exceeded':
                this.showNotification(message, 'warning', 6000);
                break;
            case 'invalid_api_key':
                this.showNotification(message, 'error', 6000);
                break;
            case 'token_limit':
                this.showNotification(message, 'warning', 5000);
                break;
            case 'network_error':
                this.showNotification(message, 'error', 4000);
                break;
            default:
                this.showNotification(message, 'error');
                break;
        }
    }

    showNotification(message, type = 'info', duration = 5000) {
        const $ = this.$;

        // Create appropriate icon based on type
        let icon = '';
        switch (type) {
            case 'warning':
                icon = '⚠️';
                break;
            case 'error':
                icon = '❌';
                break;
            case 'success':
                icon = '✅';
                break;
            default:
                icon = 'ℹ️';
                break;
        }

        // Reuse notification system from PDF export or create new one
        const notification = $(`
                <div class="swptls-notification swptls-notification-${type}">
                    <div class="swptls-notification-content">
                        <span class="swptls-notification-icon">${icon}</span>
                        <span class="swptls-notification-message">${message}</span>
                        <button class="swptls-notification-close">&times;</button>
                    </div>
                </div>
            `);

        $('body').append(notification);
        setTimeout(() => {
            notification.addClass('show');
        }, 100);

        // Auto-hide after specified duration
        setTimeout(() => {
            this.hideNotification(notification);
        }, duration);

        // Manual close button
        notification.find('.swptls-notification-close').on('click', () => {
            this.hideNotification(notification);
        });
    }

    hideNotification(notification) {
        notification.removeClass('show');
        setTimeout(() => {
            notification.remove();
        }, 300);
    }

    showRegeneratingState(button) {
        button.prop('disabled', true)
            .addClass('regenerating')
            .html(`
                    <svg class="spinner" width="16" height="16" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" stroke-dasharray="31.416" stroke-dashoffset="31.416">
                            <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                            <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                        </circle>
                    </svg>
                    🔄 Regenerating...
                `);
    }

    hideRegeneratingState(button) {
        button.prop('disabled', false)
            .removeClass('regenerating')
            .html('🔄 Regenerate Summary');
    }

    showRegeneratingInModal() {
        const $ = this.$;
        const summaryContent = $('.ai-summary-modal .summary-content');
        if (summaryContent.length) {
            summaryContent.html(`
                    <div class="regenerating-message">
                        <h3> Regenerating AI Summary...</h3>
                        <p>Analyzing current table data and generating a fresh summary. This may take a few moments.</p>
                        <div class="regenerating-dots">
                            <span>.</span><span>.</span><span>.</span>
                        </div>
                    </div>
                `);
        }
    }

    updateModalContent(summaryData, tableId) {
        const $ = this.$;
        const {
            summary,
            metadata,
            generated_at,
            tokens_used,
            model_used
        } = summaryData;

        // Update summary content
        $('.ai-summary-modal .summary-content').html(this.formatSummary(summary));

        // Update metadata
        /* const metadataGrid = $('.ai-summary-modal .metadata-grid');
        if (metadataGrid.length) {
            metadataGrid.html(`
                    <div class="metadata-item">
                        <span class="label">📊 Rows Analyzed:</span>
                        <span class="value">${metadata.rows_analyzed || 'N/A'}</span>
                    </div>
                    ${metadata.total_rows_in_dataset && metadata.total_rows_in_dataset !== metadata.rows_analyzed ? `
                    <div class="metadata-item">
                        <span class="label">📈 Total Rows:</span>
                        <span class="value">${metadata.total_rows_in_dataset}</span>
                    </div>
                    ` : ''}
                    
                    <div class="metadata-item">
                        <span class="label">🤖 Provider:</span>
                        <span class="value">${metadata.provider || 'AI'}</span>
                    </div>
                    <div class="metadata-item">
                        <span class="label">🎯 Model:</span>
                        <span class="value">${model_used || 'N/A'}</span>
                    </div>
                   
                `);
        } */
    }


    // Method to integrate with table configuration (following common_func.js pattern)
    // This should be called from table_configuration() method
    initializeForTable(tableId, table_settings) {

        // console.log(table_settings); 

        const $ = this.$;
        const container = $(`.gswpts_tables_container[id="${tableId}"]`);

        // Check if AI summary is enabled and summary_source is 'generate_on_click'
        const aiSummaryEnabled = table_settings?.enable_ai_summary;
        const summarySource = table_settings?.summary_source || 'generate_on_click';

        if (tableId && aiSummaryEnabled && summarySource === 'generate_on_click') {
            // Store table settings for this table to avoid re-parsing
            if (!window.swptlsTableSettings) {
                window.swptlsTableSettings = {};
            }
            window.swptlsTableSettings[tableId] = table_settings;

            // Note: AI Summary buttons are now rendered on the backend
            // This method now only stores settings for frontend functionality
            // console.log('AI Summary (Generate on Click) initialized for table:', tableId, 'with settings:', table_settings);
        } else if (tableId && aiSummaryEnabled && summarySource === 'instant_summary') {
            // For instant summary, we don't need frontend button but store settings for potential future use
            if (!window.swptlsTableSettings) {
                window.swptlsTableSettings = {};
            }
            window.swptlsTableSettings[tableId] = table_settings;

            // Initialize collapsed functionality if needed
            const summaryDisplay = table_settings?.summary_display || 'always_show';
            if (summaryDisplay === 'collapsed') {
                this.initializeCollapsedSummary(container);
            }

            // console.log('AI Summary (Instant Summary) initialized for table:', tableId, '- backend summary will be displayed');
        }
    }

    initializeCollapsedSummary(container) {
        const $ = this.$;

        if (!container || !container.length) {
            console.warn('AI Summary: Invalid container provided for collapsed summary initialization');
            return;
        }

        // Find collapsed summary elements in this container
        const collapsedSummaries = container.find('.swptls-backend-ai-summary.collapsed');

        if (collapsedSummaries.length === 0) {
            // console.log('AI Summary: No collapsed summaries found in container');
            return;
        }

        collapsedSummaries.each(function () {
            const summaryElement = $(this);
            const header = summaryElement.find('.summary-header.clickable');
            const content = summaryElement.find('.summary-content.collapsible');
            const expandIcon = summaryElement.find('.expand-icon');

            if (header.length === 0) {
                console.warn('AI Summary: No clickable header found for collapsed summary');
                return;
            }

            // Add click handler for expand/collapse
            header.off('click.collapsedSummary').on('click.collapsedSummary', function (e) {
                e.preventDefault();

                // Toggle the expanded state
                const isExpanded = summaryElement.hasClass('expanded');

                if (isExpanded) {
                    // Collapse
                    summaryElement.removeClass('expanded');
                    content.slideUp(300);
                    // Icon rotation handled by CSS
                } else {
                    // Expand
                    summaryElement.addClass('expanded');
                    content.slideDown(300);
                    // Icon rotation handled by CSS
                }
            });

            // Add keyboard support for accessibility
            header.attr('tabindex', '0').off('keydown.collapsedSummary').on('keydown.collapsedSummary', function (e) {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    header.click(); // Trigger the click handler
                }
            });

            // Initialize in collapsed state
            content.hide();
            // Icon state handled by CSS
        });

        // console.log(`AI Summary: Initialized ${collapsedSummaries.length} collapsed summaries`);
    }
}

// Initialize the AI Summary system outside jQuery ready block
const aiSummary = new AISummary();

// Make it globally available for other scripts
window.swptlsAISummary = aiSummary;

// Add global click handler as fallback for collapsed summaries
jQuery(document).on('click', '.swptls-backend-ai-summary.collapsed .summary-header.clickable', function (e) {
    e.preventDefault();
    // console.log('AI Summary: Global click handler triggered!');

    const header = jQuery(this);
    const summaryElement = header.closest('.swptls-backend-ai-summary');
    const content = summaryElement.find('.summary-content.collapsible');
    const expandIcon = summaryElement.find('.expand-icon');

    const isExpanded = summaryElement.hasClass('expanded');
    // console.log('AI Summary: Global handler - current state expanded:', isExpanded);

    if (isExpanded) {
        // Collapse
        summaryElement.removeClass('expanded');
        content.slideUp(300);
        // Icon state handled by CSS
        // console.log('AI Summary: Global handler - collapsing...');
    } else {
        // Expand
        summaryElement.addClass('expanded');
        content.slideDown(300);
        // Icon rotation handled by CSS
        // console.log('AI Summary: Global handler - expanding...');
    }

    if (window.swptlsBatchLoader) {
        const originalAppendBatchData = window.swptlsBatchLoader.appendBatchData;
        window.swptlsBatchLoader.appendBatchData = function (data) {
            originalAppendBatchData.call(this, data);
            // Re-initialize AI features after batch loading
            setTimeout(() => {
                $('.gswpts_tables_container').each(function () {
                    const container = $(this);
                    const tableId = container.attr('id');
                    const tableSettings = window.swptlsTableSettings?.[tableId];

                    if (tableId && tableSettings && tableSettings.show_ai_summary_button) {
                        aiSummary.initializeForTable(tableId, tableSettings);
                    }
                });
            }, 500);
        };
    }

});

// Add method to initialize AI Summary for a specific table
aiSummary.initializeForTable = function (tableId, tableSettings) {
    const $ = jQuery;
    const container = $(`.gswpts_tables_container[id="${tableId}"]`);

    // Check if AI summary is enabled for this table
    const showAISummaryButton = tableSettings?.show_ai_summary_button;
    if (tableId && (showAISummaryButton === 'true' || showAISummaryButton === true)) {
        // Store table settings for this table
        if (!window.swptlsTableSettings) {
            window.swptlsTableSettings = {};
        }
        window.swptlsTableSettings[tableId] = tableSettings;

        // console.log('AI Summary initialized for table:', tableId, 'with settings:', tableSettings);
    }
};
